import React, { useEffect, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import { useNavigate } from 'react-router-dom'

import { DOCUMENTOS_IDENTIFICACAO, TipoDocumento, TipoMiniatura, StepRefin } from '@utils/enums'
import { ApplicationState } from '@store/types'
import { Camera } from '@interco/cp-react-ui-lib'
import { setHideFooter, setHideHeader } from '@store/ui/navigation/actions'
import {
  sendToS3Foto,
  setAnexosFoto,
  setIsErrorFoto,
  setIsLoadingFoto,
  setRollbackRouterErrorFoto,
} from '@store/refinanciamento/fotoDocumentos/actions'
import { trackingRefin } from '@store/refinanciamento/simulacao/actions'
import routes from '@routes/routes'
import { Page } from '@templates'
import { useWindowSize, useTrackingRefin } from '@hooks'
import { ReviewFoto } from '@organisms'
import Tag from '@utils/Tag'
import { BridgeService } from '@services/bridge'

const Foto = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  useTrackingRefin(StepRefin.FOTO_DOCUMENTO)
  const [habilitaCamera, setHabilitaCamera] = useState(true)
  const [foto, setFoto] = useState('')
  const [descricaoFoto, setDescricaoFoto] = useState('Frente')
  const [fotoFrente, setFotoFrente] = useState(false)
  const [fullScreenMobile, setFullScreenMobile] = useState(false)

  const { anexos, isLoading, isDone } = useSelector(
    (state: ApplicationState) => state.refinanciamento.fotoDocumentos,
  )
  const { tipoDocumento } = useSelector(
    (state: ApplicationState) => state.refinanciamento.documentos,
  )
  const { isApp } = useSelector((state: ApplicationState) => state.ui.navigation)

  const size = useWindowSize()

  const onConfirmReview = (fotografia: string) => {
    dispatch(trackingRefin(StepRefin.CLICK_FOTO_DOCUMENTO_USAR_FOTO))
    if (isApp) {
      Tag.enviarTagueamentoApp({
        screen: 'clt:refin:documents',
        ref_type: '',
        content_action: 'Dado carregado',
        action_id: 'Foto do documento',
        section: 'documents',
        sub_section: '',
        step: '',
        scenario: 'Foto do documento',
        ref_figma: '2',
      })
    } else {
      Tag.enviarTagueamentoWeb({
        element_action: 'page_loaded',
        element_name: 'Foto do documento',
        c_page: window.location.href,
        redirect_url: routes.STATUS_DOCUMENTO,
        section_name: 'payrollloan:refin:upload',
        section: 'upload',
        sub_section: '',
        step: '',
        scenario: 'Foto do documento',
      })
    }

    if (anexos.length === 2) {
      dispatch(setIsLoadingFoto(true))
      dispatch(setIsErrorFoto(false))
      dispatch(setRollbackRouterErrorFoto(routes.STATUS_DOCUMENTO))
      dispatch(
        sendToS3Foto({
          tipo: 'RG_CNH',
          anexos,
        }),
      )
    } else {
      const img = new Image()
      img.onload = () => {
        dispatch(
          setAnexosFoto({
            anexo: {
              id: uuidv4(),
              nome: `RG/CNH.jpeg`,
              tipoExtensao: 'jpeg',
              blobs: [
                {
                  height: img.height,
                  width: img.width,
                  data: fotografia,
                },
              ],
            },
            tipo: 'RG_CNH',
          }),
        )

        if (anexos.length === 2) {
          dispatch(setIsLoadingFoto(true))
          dispatch(setIsErrorFoto(false))
          dispatch(setRollbackRouterErrorFoto(routes.STATUS_DOCUMENTO))
          dispatch(
            sendToS3Foto({
              tipo: 'RG_CNH',
              anexos,
            }),
          )
        }
        setFotoFrente(true)
        setDescricaoFoto('Verso')
      }

      img.src = fotografia
    }
  }

  const documentType = DOCUMENTOS_IDENTIFICACAO.find(({ value }) => value === tipoDocumento)

  const tipoTexto = () => {
    const documentName = documentType?.name
    if (!documentName) return `${descricaoFoto} do documento`

    const article = documentName.startsWith('Carteira') ? 'da' : 'do'

    return `${descricaoFoto} ${article} ${documentName}`
  }

  const tipoMiniatura = () => {
    if (descricaoFoto === 'Frente') {
      if (tipoDocumento === TipoDocumento.RG) return TipoMiniatura.RG_FRENTE
      if (tipoDocumento === TipoDocumento.CNH) return TipoMiniatura.CNH_FRENTE
      return TipoMiniatura.RNE_FRENTE
    }

    if (tipoDocumento === TipoDocumento.RG) return TipoMiniatura.RG_VERSO
    if (tipoDocumento === TipoDocumento.CNH) return TipoMiniatura.CNH_VERSO
    return TipoMiniatura.RNE_VERSO
  }

  useEffect(() => {
    BridgeService.tac({
      name: 'foto_documento',
      actions: ['tirar_foto', 'anexar_foto', 'selecionar_arquivo_documento'],
    })
  }, [])

  useEffect(() => {
    if (size.width < 680) {
      setFullScreenMobile(true)
    } else {
      setFullScreenMobile(false)
    }
    return () => {
      setFullScreenMobile(false)
    }
  }, [size])

  useEffect(() => {
    if (fotoFrente) setHabilitaCamera(true)
  }, [fotoFrente])

  useEffect(() => {
    if (isDone) {
      if (isApp) {
        Tag.enviarTagueamentoApp({
          screen: 'clt:refin:documents',
          ref_type: '',
          content_action: 'Dado carregado',
          action_id: 'Foto do documento',
          section: 'documents',
          sub_section: 'tips',
          step: '',
          scenario: 'Foto do documento',
          ref_figma: '2',
        })
      } else {
        Tag.enviarTagueamentoWeb({
          element_action: 'page_loaded',
          element_name: 'Foto do documento',
          c_page: window.location.href,
          redirect_url: routes.STATUS_DOCUMENTO,
          section_name: 'payrollloan:refin:upload',
          section: 'upload',
          sub_section: 'tips',
          step: '',
          scenario: 'Foto do documento',
        })
      }
      navigate(routes.STATUS_DOCUMENTO)
    }
  }, [navigate, isDone, isApp])

  useEffect(() => {
    if (isApp || fullScreenMobile) {
      dispatch(setHideFooter(true))
      dispatch(setHideHeader(true))
    } else {
      dispatch(setHideFooter(false))
      dispatch(setHideHeader(false))
    }
  }, [dispatch, fullScreenMobile, isApp])

  return habilitaCamera ? (
    <Page>
      <Camera
        audio={false}
        mask="secondary"
        mode="photo"
        facing="back"
        mobile={isApp || fullScreenMobile}
        onCapturedPhoto={(fotografia) => {
          setHabilitaCamera(false)
          setFoto(String(fotografia))
        }}
        titleDescription={tipoTexto()}
        imagemMiniatura={tipoMiniatura()}
      />
    </Page>
  ) : (
    <ReviewFoto
      fotografia={foto}
      onConfirm={onConfirmReview}
      setHabilitaCamera={() => {
        setHabilitaCamera(true)
      }}
      isLoading={isLoading}
      forceIsMobile={fullScreenMobile}
    />
  )
}

export default Foto
